import { afterNextPaint } from '@helpers/after-next-paint';
import { useConversationUrl } from '@hooks/use-conversation-url';
import { useAppSelector } from '@redux/hooks';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import { generateDefaultField } from '@tkmp/rfq/utils';
import { Link } from '@tkww/orion-web-ds-action';
import type { InlineRfqSourceContent } from '@typings/sourceContent';
import { Body1 } from '@xo-union/tk-ui-typography';
import React from 'react';
import Styles from './highlightedRequest.scss';
import { HighlightedRequestIcon } from './highlightedRequestIcon';

export type HighlightedRequestProps = {
	messageText: string;
	buttonText: string;
	initiator: InlineRfqSourceContent;
	hideIfLead?: boolean;
};

export const HighlightedRequest: React.FC<HighlightedRequestProps> = ({
	messageText,
	buttonText,
	initiator,
	hideIfLead,
}) => {
	const vendorId = useAppSelector((state) => state.vendor.vendor?.id);
	const conversationUrl = useConversationUrl(vendorId);

	const localFormContext = useInlineRfqForm({
		freeTextLabel: '',
		headerText: messageText,
		textAreaInitialValueOverride: generateDefaultField('textarea'),
		initiator,
	});

	if (hideIfLead && conversationUrl) {
		return null;
	}

	const containerTestId = `${initiator.toLowerCase().replace(' ', '-')}-container`;

	return (
		<>
			<RfqModal context={localFormContext} />
			<div className={Styles.container} data-testid={containerTestId}>
				<HighlightedRequestIcon />
				<div className={Styles.rightContent}>
					<Body1>{messageText}</Body1>
					{conversationUrl ? (
						<Link
							styleType="secondary"
							href={conversationUrl}
							size="md"
							className={Styles.button}
						>
							View Conversation
						</Link>
					) : (
						<Link
							as="button"
							onClick={() => {
								afterNextPaint(() => {
									localFormContext.openModal();
								});
							}}
							className={Styles.button}
							type="button"
						>
							{buttonText}
						</Link>
					)}
				</div>
			</div>
		</>
	);
};
